import {jwtDecode} from 'jwt-decode';
import {create, type StateCreator} from 'zustand';
import {devtools, persist} from 'zustand/middleware';

import {toast} from '../../../ui-lib/components';
import {type Role} from '../constants/roles';
import {config} from '../services/configService';

export interface AuthStoreState {
  didValidate: boolean;
  roles: string[];
  expiresAt?: Date | undefined;
  token: string | undefined;
  decodedToken: object | undefined;
  refreshToken: string | undefined;
}

export interface AuthStoreActions {
  reset: () => void;
  checkRoles: (...args: Role[]) => boolean;
  setDidValidate: (didValidate: AuthStoreState['didValidate']) => void;
  setTokens: (
    token: AuthStoreState['token'],
    refreshToken: AuthStoreState['refreshToken'],
    expiresAt: AuthStoreState['expiresAt'],
  ) => void;
  logout: () => void;
}

const authStoreInitialState: AuthStoreState = {
  didValidate: false,
  roles: [],
  expiresAt: undefined,
  token: undefined,
  refreshToken: undefined,
  decodedToken: undefined,
};

const getRolesFromToken = (jwtToken: object): string[] => {
  const clientId = config.sso.apiClientId as unknown;

  if (
    'resource_access' in jwtToken &&
    jwtToken.resource_access != null &&
    typeof jwtToken.resource_access === 'object' &&
    clientId != null &&
    typeof clientId === 'string' &&
    clientId in jwtToken.resource_access &&
    (jwtToken.resource_access as never)[clientId] != null &&
    typeof (jwtToken.resource_access as never)[clientId] === 'object' &&
    'roles' in (jwtToken.resource_access as never)[clientId] &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Array.isArray((jwtToken.resource_access as any)[clientId].roles)
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (jwtToken.resource_access as any)[clientId].roles;
  }
  return [];
};

export type AuthStore = AuthStoreState & AuthStoreActions;

const authStoreHandler: StateCreator<AuthStore> = (setState, getState) => ({
  ...authStoreInitialState,
  reset: (): void => {
    setState(authStoreInitialState);
  },
  setDidValidate: (didValidate): void => {
    setState({didValidate});
  },
  setTokens: (token, refreshToken, expiresAt): void => {
    const jwtToken = token ? jwtDecode(token) : null;
    const roles = jwtToken ? getRolesFromToken(jwtToken) : [];
    setState({
      token,
      roles,
      refreshToken,
      decodedToken: token ? jwtDecode(token) : undefined,
      expiresAt,
    });
  },
  checkRoles: (...args): boolean => {
    if (args.length === 0) {
      return true;
    }

    const {token, roles, decodedToken, expiresAt} = getState();

    if (expiresAt && expiresAt <= new Date()) {
      toast('Token expired', 'error');
      return false;
    }

    let jwtToken: unknown = null;

    if (decodedToken) {
      jwtToken = decodedToken;
    } else if (token) {
      jwtToken = jwtDecode(token);
    }

    let userRoles = roles;

    if (userRoles.length === 0 && typeof jwtToken === 'object' && jwtToken) {
      userRoles = getRolesFromToken(jwtToken);
    }

    if (userRoles.length > 0) {
      return args.every((arg) => userRoles.includes(arg));
    }

    return false;
  },
  logout: (): void => {
    // resetAllStores();
    setState(authStoreInitialState);
    localStorage.removeItem('auth');
  },
});

const AuthStoreMiddleWare = (
  f: StateCreator<AuthStore>,
): StateCreator<
  AuthStore,
  [],
  [
    ['zustand/devtools', never],
    [
      'zustand/persist',
      {
        token: string | undefined;
        refreshToken: string | undefined;
        expiresAt: Date | undefined;
      },
    ],
  ]
> =>
  devtools(
    persist(f, {
      name: 'auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AuthStoreState>;
        return {
          ...currentState,
          ...persisted,
          expiresAt:
            persisted.expiresAt != null
              ? new Date(persisted.expiresAt)
              : undefined,
        };
      },
    }),
  );

export const useAuthStore = create<AuthStore>()(
  AuthStoreMiddleWare(authStoreHandler),
);
